import classNames from 'classnames'
import React, { HTMLAttributes, forwardRef } from 'react'

import { TextColorType } from '@ui/design-system/colors/colors'

import styles from './Typography.module.scss'

export type HTMLTag =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
  | 'label'
  | 'aside'

export type AlignType = 'left' | 'center' | 'right'

type OneWeight =
  | { medium?: true; semibold?: never; bold?: never }
  | { medium?: never; semibold?: true; bold?: never }
  | { medium?: never; semibold?: never; bold?: true }
  | { medium?: never; semibold?: never; bold?: never }

type BaseProps = Omit<HTMLAttributes<HTMLElement>, 'className'> &
  OneWeight & {
    id?: string
    children?: React.ReactNode
    className?: string
    lowercase?: boolean
    uppercase?: boolean
    color?: TextColorType
    as?: HTMLTag

    isParagraph?: boolean
    align?: AlignType
  }

type WeightKey = 'normal' | 'medium' | 'semibold' | 'bold'
const resolveWeight = (p: OneWeight): WeightKey =>
  (p as any).medium
    ? 'medium'
    : (p as any).semibold
    ? 'semibold'
    : (p as any).bold
    ? 'bold'
    : 'normal'

type Role =
  | 'display-1'
  | 'display-2'
  | 'display-3'
  | 'heading-1'
  | 'heading-2'
  | 'heading-3'
  | 'heading-4'
  | 'heading-5'
  | 'heading-6'
  | 'title-1'
  | 'title-2'
  | 'body-1'
  | 'body-2'
  | 'caption-1'
  | 'caption-2'
  | 'overline'

const DEFAULT_TAG: Record<Role, HTMLTag> = {
  'display-1': 'h1',
  'display-2': 'h1',
  'display-3': 'h1',
  'heading-1': 'h1',
  'heading-2': 'h2',
  'heading-3': 'h3',
  'heading-4': 'h4',
  'heading-5': 'h5',
  'heading-6': 'h6',
  'title-1': 'span',
  'title-2': 'span',
  'body-1': 'span',
  'body-2': 'span',
  'caption-1': 'span',
  'caption-2': 'span',
  overline: 'span',
}

function makeTypography<R extends Role>(role: R) {
  const Comp = forwardRef<HTMLElement, BaseProps>(function Typography(
    { as, isParagraph, className, children, align, lowercase, uppercase, color, ...rest },
    ref
  ) {
    const weight = resolveWeight(rest)
    const Tag = (isParagraph ? 'p' : as ?? DEFAULT_TAG[role]) as any

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { medium, semibold, bold, style, ...dom } = rest as any

    const cls = classNames(
      styles[`t-${role}`],
      weight !== 'normal' && styles[`t-${role}--${weight}`],
      align === 'left' && styles.alignLeft,
      align === 'center' && styles.alignCenter,
      align === 'right' && styles.alignRight,
      lowercase && styles.lowercase,
      uppercase && styles.uppercase,
      color,
      className
    )

    return (
      <Tag ref={ref as any} className={cls} {...dom}>
        {children}
      </Tag>
    )
  })

  Comp.displayName = role
    .split('-')
    .map((s) => s[0].toUpperCase() + s.slice(1))
    .join('')

  return Comp
}

export const Display1 = makeTypography('display-1')
export const Display2 = makeTypography('display-2')
export const Display3 = makeTypography('display-3')

export const Heading1 = makeTypography('heading-1')
export const Heading2 = makeTypography('heading-2')
export const Heading3 = makeTypography('heading-3')
export const Heading4 = makeTypography('heading-4')
export const Heading5 = makeTypography('heading-5')
export const Heading6 = makeTypography('heading-6')

export const Title1 = makeTypography('title-1')
export const Title2 = makeTypography('title-2')

export const Body1 = makeTypography('body-1')
export const Body2 = makeTypography('body-2')

export const Caption1 = makeTypography('caption-1')
export const Caption2 = makeTypography('caption-2')

export const Overline = makeTypography('overline')
